import {
  Component,
  Input,
  OnInit,
  forwardRef,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import * as moment from 'moment';

@Component({
  selector: 'datetime-range-picker',
  templateUrl: './datetime-range-picker.component.html',
  styleUrls: ['./datetime-range-picker.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DatetimeRangePickerComponent),
      multi: true,
    },
  ],
})
export class DatetimeRangePickerComponent implements OnInit, ControlValueAccessor {
  @Input() timePicker = false;
  @Input() timePicker24Hour = true;
  @Input() showDropdowns = true;
  @Input() linkedCalendars = false;

  @Input() opens: 'left' | 'right' | 'center' = 'center';
  @Input() drops: 'up' | 'down' = 'down';

  @Input() firstDayOfWeek = 1;
  @Input() minDate: moment.Moment = null;

  @Input() customPresets: string[] = [
    'Today',
    'Yesterday and today',
    'Yesterday',
    'Last 7 days',
    'This week',
    'Last week',
    'This month',
    'Previous month',
  ];

  defaultPresets: string[] = [
    'Today',
    'Yesterday and today',
    'Yesterday',
    'Last 7 days',
    'This week',
    'Last week',
    'This month',
    'Previous month',
  ];

  private readonly pickerWidthPx = 860;
  selected: { startDate: moment.Moment; endDate: moment.Moment };
  maxDate = moment();
  ranges: any = {};
  locale: any = {};

  onChange: any = () => {};
  onTouched: any = () => {};

  isInvalidDate = (date: moment.Moment) => {
    return this.minDate ? date.isBefore(this.minDate, 'day') : false;
  };

  private allPresets: { [key: string]: [moment.Moment, moment.Moment] } = {};

  ngOnInit() {
    // Defensive only. With [ngModel] Angular always calls writeValue AFTER ngOnInit (verified:
    // ngOnInit -> writeValue(null) -> writeValue(value)), so today this never has anything to
    // preserve. What fixes the today - today symptom is the one-way inner [ngModel] binding in
    // the template. This guard just makes sure ngOnInit can never destroy a selection a caller
    // already supplied, should the ordering ever differ (reactive forms, a manual writeValue).
    this.selected ??= {
      startDate: moment().startOf('day'),
      endDate: moment(),
    };

    this.allPresets = {
      'Today': [moment().startOf('day'), moment().endOf('day')],
      'Yesterday and today': [moment().subtract(1, 'days').startOf('day'), moment().endOf('day')],
      'Yesterday': [moment().subtract(1, 'days').startOf('day'), moment().subtract(1, 'days').endOf('day')],
      'Last 7 days': [moment().subtract(6, 'days').startOf('day'), moment().endOf('day')],
      'This week': [moment().startOf('isoWeek'), moment().endOf('day')],
      'Last week': [moment().subtract(1, 'week').startOf('isoWeek'), moment().subtract(1, 'week').endOf('isoWeek')],
      'This month': [moment().startOf('month'), moment().endOf('day')],
      'Previous month': [moment().subtract(1, 'month').startOf('month'), moment().subtract(1, 'month').endOf('month')],
      'This year': [moment().startOf('year'), moment().endOf('day')],
      'Current year': [moment().startOf('year'), moment().endOf('day')],
      'Previous year': [moment().subtract(1, 'year').startOf('year'), moment().subtract(1, 'year').endOf('year')],
    };

    this.ranges = {};
    if (!this.customPresets?.length) {
      this.customPresets = [...this.defaultPresets];
    }

    this.customPresets.forEach(preset => {
      if (this.allPresets[preset]) {
        this.ranges[preset] = this.allPresets[preset];
      }
    });

    this.locale = {
      format: this.timePicker ? 'DD/MM/YYYY HH:mm' : 'DD/MM/YYYY',
      separator: ' - ',
      applyLabel: 'APPLY',
      cancelLabel: 'CANCEL',
      customRangeLabel: 'Custom range',
      firstDay: this.firstDayOfWeek,
      daysOfWeek: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
      monthNames: [
        'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
      ],
    };
  }

  onDatesUpdated(event: { startDate: moment.Moment; endDate: moment.Moment }) {
    if (!event.startDate || !event.endDate){
      return;
    }

    const start = this.timePicker
      ? event.startDate.clone()
      : event.startDate.clone().startOf('day');

    const end = this.timePicker
      ? event.endDate.clone()
      : event.endDate.clone().endOf('day');

    this.selected = { startDate: start, endDate: end };

    const endForApi = this.timePicker
      ? event.endDate.clone()
      : event.endDate.clone().startOf('day');

    this.onChange({
      start: start.toDate(),
      end: endForApi.toDate(),
    });
    this.onTouched();
  }

  writeValue(value: any): void {
    if (value && value.start && value.end) {
      const start = moment(this.toPickerInput(value.start));
      const end = moment(this.toPickerInput(value.end));

      this.selected = {
        startDate: this.timePicker ? start : start.clone().startOf('day'),
        endDate: this.timePicker
          ? end
          : end.clone().endOf('day'),
      };
    }
  }

  /**
   * The inner picker renders every date with `dayjs(value).utc(true)`: it keeps the LOCAL wall
   * clock and labels it UTC. `onDatesUpdated` then emits that UTC instant back to the caller, so
   * an emitted value carries the time the user saw in its UTC part, not its local one. Reading it
   * back with a plain `moment(date)` would take the local part instead and the range would gain
   * the timezone offset on every round trip through the model (+3h in UTC+3) - which is exactly
   * what happens once a filter is persisted and restored. Re-anchor the instant so that its local
   * wall clock equals the UTC wall clock the picker is about to display.
   *
   * Only relevant when a time is selectable: date-only pickers normalise their value to day
   * boundaries, so they already round-trip within the same calendar day.
   */
  private toPickerInput(value: Date | string): Date {
    const date = value instanceof Date ? value : new Date(value);
    if (!this.timePicker || Number.isNaN(date.getTime())) {
      return date;
    }

    return new Date(date.getTime() + date.getTimezoneOffset() * 60000);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  updateOpens(inputEl: HTMLInputElement) {
    if (!inputEl) return;

    const rect = inputEl.getBoundingClientRect();
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;

    const gutter = 12;
    const spaceLeft = rect.left - gutter;
    const spaceRight = viewportWidth - rect.right - gutter;

    if (spaceRight < this.pickerWidthPx && spaceLeft >= this.pickerWidthPx) {
      this.opens = 'left';
      return;
    }

    if (spaceLeft < this.pickerWidthPx && spaceRight >= this.pickerWidthPx) {
      this.opens = 'right';
      return;
    }

    if (spaceLeft < this.pickerWidthPx && spaceRight < this.pickerWidthPx) {
      this.opens = spaceRight >= spaceLeft ? 'right' : 'left';
      return;
    }

    this.opens = 'center';
  }
}